import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CheckSquare, Plus, Check, X, Trash2, Pencil, GripVertical, Settings2 } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "../components/ui/sonner";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TarefasConfigPage() {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editType, setEditType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: "" });
  const dragItem = useRef(null);
  const dragOver = useRef(null);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";

  const fetchTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/task-types`, { withCredentials: true });
      setTypes(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Informe o nome do tipo"); return; }
    try {
      if (editType) {
        await axios.put(`${API}/task-types/${editType.id}`, { name: form.name }, { withCredentials: true });
        toast.success("Tipo atualizado");
      } else {
        await axios.post(`${API}/task-types`, { name: form.name }, { withCredentials: true });
        toast.success("Tipo criado");
      }
      setDialogOpen(false);
      fetchTypes();
    } catch (err) { toast.error(t("ui.erro_ao_salvar")); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/task-types/${deleteTarget.id}`, { withCredentials: true });
      setDeleteTarget(null); fetchTypes();
    } catch (err) { toast.error("Erro ao excluir"); }
  };

  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOver.current === null) return;
    const items = [...types];
    const [dragged] = items.splice(dragItem.current, 1);
    items.splice(dragOver.current, 0, dragged);
    const reordered = items.map((item, i) => ({ ...item, order: i }));
    setTypes(reordered);
    dragItem.current = null; dragOver.current = null;
    try {
      await axios.put(`${API}/task-types-order`, { order: reordered.map(tt => ({ id: tt.id, order: tt.order })) }, { withCredentials: true });
    } catch (err) { console.error(err); }
  };

  return (
    <div data-testid="tarefas-config-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <Settings2 className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">{t("configuracoes.tarefasConfig.title")}</h1>
            <p className="text-slate-500 mt-0.5">{t("configuracoes.tarefasConfig.subtitle")}</p>
          </div>
        </div>
        <Tooltip><TooltipTrigger asChild><Button onClick={() => { setEditType(null); setForm({ name: "" }); setDialogOpen(true); }} size="icon" style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90 h-9 w-9"><Plus className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("common.new")}</TooltipContent></Tooltip>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <CheckSquare className="w-4 h-4" style={{ color: tc500 }} />{t("ui.tipos_de_tarefa")}</p>
          <p className="text-xs text-slate-400 mt-0.5">Arraste para reordenar. Estes tipos aparecem no formulário de criação de tarefas.</p>
        </div>
        <div className="p-3 space-y-2">
          {loading ? (
            [...Array(5)].map((_, i) => <div key={i} className="h-12 bg-slate-50 rounded-lg animate-pulse" />)
          ) : types.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">{t("ui.nenhum_tipo")}</div>
          ) : types.map((type, i) => (
            <div key={type.id} draggable
              onDragStart={() => { dragItem.current = i; }}
              onDragEnter={() => { dragOver.current = i; }}
              onDragEnd={handleDragEnd}
              onDragOver={e => e.preventDefault()}
              className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2.5 cursor-grab active:cursor-grabbing hover:bg-slate-100 transition-colors">
              <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${tc500}18` }}>
                <CheckSquare className="w-4 h-4" style={{ color: tc500 }} />
              </div>
              <span className="flex-1 text-sm font-medium text-slate-800">{type.name}</span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => { setEditType(type); setForm({ name: type.name }); setDialogOpen(true); }} className="text-sky-600 hover:text-sky-700 hover:bg-sky-50 h-7 w-7 p-0">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(type)} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 h-7 w-7 p-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CheckSquare className="w-5 h-5" style={{ color: tc500 }} />{editType ? "Editar Tipo" : "Novo Tipo"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className="text-slate-700">{t("common.name")}<span className="text-rose-500">*</span></Label>
              <Input value={form.name} onChange={e => setForm({ name: e.target.value })} placeholder="Ex: Ligação, Reunião..." required /></div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}><X className="w-4 h-4 mr-2" />{t("common.cancel")}</Button>
              <Button type="submit" className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }}><Check className="w-4 h-4 mr-2" />{editType ? "Salvar" : "Criar"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)} title="Excluir tipo" description={`${t("common.confirm_delete")} o tipo "${deleteTarget?.name}"?`} onConfirm={handleDelete} />
    </div>
  );
}
